import { createColumnHelper } from '@tanstack/react-table';
import { CompanyType } from '@va/dashboard/api-client/sessions.api';
import { isCompanyRevealFeatureEnabled } from '@va/dashboard/selectors/app';
import { CompanyRevealLabel, CompanyRevealPreview, CompanySessionsItem } from '@va/dashboard/shared/visitors';
import { LinkIcon } from '@va/icons';
import { useLocalizationContext, useTranslate } from '@va/localization';
import {
  fontWeights,
  IconWithBackground,
  ListCardItemWithIcon,
  Paragraph,
  paragraphSizes,
  ProgressBar,
} from '@va/ui/design-system';
import { addNumberSeparator } from '@va/util/helpers';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

const columnHelper = createColumnHelper<CompanySessionsItem>();
export const useVisitsByCompanyColumns = () => {
  const { locale, translate } = useLocalizationContext();
  return useMemo(
    () => [
      columnHelper.display({
        id: 'title',
        meta: {
          useDefaultCellStyle: false,
          cellClassName: 'justify-start',
        },
        cell: ({
          row: {
            original: { companyOrgName, companyOrgType },
          },
        }) => (
          <ListCardItemWithIcon
            icon={<IconWithBackground className='!bg-white-snow group-hover:!bg-white' icon={() => <LinkIcon />} />}
            topTextTooltipProps={{
              content: (
                <TitleTopText companyName={companyOrgName} companyType={companyOrgType} className='text-white' />
              ),
            }}
            topText={<TitleTopText companyName={companyOrgName} companyType={companyOrgType} />}
          />
        ),
      }),
      columnHelper.display({
        id: 'progress',
        cell: ({
          row: {
            original: { sessionPercentOfTotal },
          },
        }) => (
          <div className='w-full'>
            <ProgressBar progress={[{ percentage: sessionPercentOfTotal, color: window.PRIMARY_COLOR }]} />
          </div>
        ),
      }),
      columnHelper.display({
        id: 'percentage',
        meta: {
          useDefaultCellStyle: false,
          cellClassName: 'justify-end',
        },
        cell: ({
          row: {
            original: { sessionPercentOfTotal, sessionCount },
          },
        }) => {
          return (
            <div className={'text-right'}>
              <Paragraph weight={fontWeights.medium}>
                {addNumberSeparator(sessionPercentOfTotal, locale, { style: 'percent', signDisplay: 'never' })}
              </Paragraph>
              <Paragraph weight={fontWeights.medium} size={paragraphSizes.tiny}>
                {translate('sessionsCount', { count: addNumberSeparator(sessionCount, locale) })}
              </Paragraph>
            </div>
          );
        },
      }),
    ],
    [locale, translate],
  );
};

type TitleTopTextProps = {
  companyName: string | null;
  companyType: CompanyType | null;
  className?: string;
};

const TitleTopText = ({ companyName, companyType, className }: TitleTopTextProps) => {
  const translate = useTranslate();
  const showIp2Company = useSelector(isCompanyRevealFeatureEnabled);

  return showIp2Company ? (
    <CompanyRevealLabel
      companyName={companyName || translate('labels.unknown')}
      companyType={companyType}
      className={className}
    />
  ) : (
    <CompanyRevealPreview />
  );
};
